import { Link, Outlet, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/session';

/**
 * Layout for the authenticated shell: the top brand/nav header and a `<main>`
 * that hosts any child route via `<Outlet />`.
 *
 * The `/login` route is deliberately a sibling (not a descendant) of this
 * layout — the login page is standalone and renders without any header chrome
 * at all. Keeping that invariant structural (vs CSS-hidden) means a visitor
 * cannot reveal the nav by inspecting the DOM.
 *
 * Auth-aware nav semantics:
 *   - `authenticated` — shows the "Log out" control on the right.
 *   - `loading` / `anonymous` — shows no auth control. In practice the
 *     `anonymous` case isn't reachable with this layout mounted (RequireAuth
 *     redirects to the bare `/login` route before this renders), but the
 *     `loading` flash is — RequireAuth renders its placeholder inside this
 *     layout while `/auth/me` is in flight, and we don't want a "Log out"
 *     button to appear for an unauthenticated user even briefly.
 *
 * Role-aware nav semantics:
 *   - `Runs` and `Log out` show for any authenticated user.
 *   - `Customers` and `Users` show only for an ADMIN. This is cosmetic — the
 *     API is the real authorization gate (a STANDARD user who types
 *     `/customers` is stopped server-side); hiding the links just keeps the
 *     nav honest about what the user can do. `user` is `null` while loading /
 *     anonymous, so the `?.` short-circuit means the ADMIN links never leak
 *     before auth, consistent with the no-chrome-before-auth invariant above.
 */
export function AppLayout(): JSX.Element {
  const { status, user, logout } = useAuth();
  const navigate = useNavigate();

  const isAdmin = user?.role === 'ADMIN';

  const handleLogout = async (): Promise<void> => {
    // `logout` already swallows expected errors (e.g. an already-expired
    // session 401) and clears local state unconditionally, so we redirect
    // regardless of the call's outcome. `replace: true` prevents the back
    // button from bouncing the user straight back into an authenticated
    // route that would then immediately redirect them here again.
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="bg-brand-teal text-white p-4">
        <nav className="flex gap-4 items-center">
          <Link to="/">DragonIS CDR</Link>
          <Link to="/runs">Runs</Link>
          {isAdmin ? <Link to="/customers">Customers</Link> : null}
          {isAdmin ? <Link to="/users">Users</Link> : null}
          {status === 'authenticated' ? (
            <button
              type="button"
              onClick={() => {
                void handleLogout();
              }}
              className="ml-auto text-white hover:underline focus:outline-none focus-visible:underline"
            >
              Log out
            </button>
          ) : null}
        </nav>
      </header>
      <main className="p-6">
        <Outlet />
      </main>
    </div>
  );
}
